'use client';

import { useMemo, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import type { AiComponentDto } from '@terra/shared-contracts-ai-applications';
import { getAiComponentTypeIcon } from '@terra/ui-ai-applications';
import { GroupHeaderCell } from '@terra/ui-design-system';
import { ListPageCellFrame } from '../../common/ListPageCellFrame';
import { rowClickBlockerProps } from '../rowClickBlocker';
import {
  AiComponentCoverageBadge,
  AiComponentDateCell,
  AiComponentDetectionCell,
  AiComponentNameCell,
  AiComponentTextCell,
  AiComponentTypeBadge,
  AiComponentVerificationBadge,
} from './AiComponentsTableCells';
import {
  isAiComponentsGroupHeaderRow,
  isAiComponentsSortColumn,
  type AiComponentsTableRow,
} from './ai-components-table.model';

const NAME_COLUMN_MIN_WIDTH_PX = 240;
const NAME_COLUMN_ID = 'name';

const preserveServerSortOrder = () => 0;

export type AiComponentDetectionCounts = {
  signalCountsByComponentId: Readonly<Record<string, number>>;
  findingCountsByComponentId: Readonly<Record<string, number>>;
  isSignalsUnavailable: boolean;
  isFindingsUnavailable: boolean;
};

type AiComponentValueColumn = {
  colId: string;
  headerName: string;
  width: number;
  render: (component: AiComponentDto) => ReactNode;
};

export function useAiComponentsTableColumnDefs({
  detections,
  collapsedGroupKeys,
  onToggleGroup,
}: {
  detections: AiComponentDetectionCounts;
  collapsedGroupKeys: ReadonlySet<string>;
  onToggleGroup: (groupKey: string) => void;
}): ColDef<AiComponentsTableRow>[] {
  const tAssets = useTranslations('aiApplications.assets');
  const tDrawer = useTranslations('aiApplications.componentDrawer');
  const tTable = useTranslations('proxy.assets.table');

  return useMemo(() => {
    const valueColumnsConfig: AiComponentValueColumn[] = [
      {
        colId: 'type',
        headerName: tAssets('type'),
        width: 160,
        render: (component) => <AiComponentTypeBadge type={component.type} />,
      },
      {
        colId: 'role',
        headerName: tAssets('role'),
        width: 168,
        render: (component) => <AiComponentTextCell value={component.role} />,
      },
      {
        colId: 'isInternal',
        headerName: tAssets('coverage'),
        width: 132,
        render: (component) => (
          <AiComponentCoverageBadge isInternal={component.isInternal} />
        ),
      },
      {
        colId: 'signals',
        headerName: tAssets('signals'),
        width: 120,
        render: (component) => (
          <AiComponentDetectionCell
            type="signal"
            count={detections.signalCountsByComponentId[component.id] ?? 0}
            isUnavailable={detections.isSignalsUnavailable}
          />
        ),
      },
      {
        colId: 'findings',
        headerName: tAssets('findings'),
        width: 120,
        render: (component) => (
          <AiComponentDetectionCell
            type="finding"
            count={detections.findingCountsByComponentId[component.id] ?? 0}
            isUnavailable={detections.isFindingsUnavailable}
          />
        ),
      },
      {
        colId: 'verificationStatus',
        headerName: tDrawer('verificationStatus'),
        width: 148,
        render: (component) => (
          <AiComponentVerificationBadge component={component} />
        ),
      },
      {
        colId: 'createdAt',
        headerName: tTable('createdAt'),
        width: 168,
        render: (component) => (
          <AiComponentDateCell value={component.createdAt} />
        ),
      },
    ];

    const valueColumns = valueColumnsConfig.map(
      ({ colId, headerName, width, render }): ColDef<AiComponentsTableRow> => ({
        colId,
        headerName,
        width,
        minWidth: width,
        sortable: isAiComponentsSortColumn(colId),
        comparator: preserveServerSortOrder,
        cellRenderer: (params: ICellRendererParams<AiComponentsTableRow>) => {
          const row = params.data;

          return row && !isAiComponentsGroupHeaderRow(row) ? (
            <ListPageCellFrame>{render(row)}</ListPageCellFrame>
          ) : null;
        },
      }),
    );

    const nameColumn: ColDef<AiComponentsTableRow> = {
      colId: NAME_COLUMN_ID,
      headerName: tTable('name'),
      flex: 1,
      minWidth: NAME_COLUMN_MIN_WIDTH_PX,
      lockPosition: 'left',
      sortable: true,
      comparator: preserveServerSortOrder,
      colSpan: (params) =>
        isAiComponentsGroupHeaderRow(params.data) ? valueColumns.length + 1 : 1,
      cellRenderer: (params: ICellRendererParams<AiComponentsTableRow>) => {
        if (isAiComponentsGroupHeaderRow(params.data)) {
          const GroupIcon = params.data.componentType
            ? getAiComponentTypeIcon(params.data.componentType)
            : null;

          return (
            <GroupHeaderCell
              group={params.data}
              isCollapsed={collapsedGroupKeys.has(params.data.groupKey)}
              onToggle={onToggleGroup}
              containerProps={rowClickBlockerProps}
              icon={GroupIcon ? <GroupIcon size={16} /> : undefined}
            />
          );
        }

        return params.data ? (
          <AiComponentNameCell component={params.data} />
        ) : null;
      },
    };

    return [nameColumn, ...valueColumns];
  }, [collapsedGroupKeys, detections, onToggleGroup, tAssets, tDrawer, tTable]);
}
